import { useEffect, useState } from 'react';

import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../../common/fetch/fetch-agg-response';
import { logger } from '../../../../common/utils/logger';

import { CONFLUENCE_PAGE_AI_SNIPPET_OPERATION_NAME, KnowledgeDiscoveryAiSnippetQuery } from './gql';
import type { ConfluencePageAiSnippetResponse, ConfluencePageAiSnippetVariables } from './types';

const fetchConfluenceAiSnippet = async ({
	variables,
	workspaceId,
	product,
}: {
	variables: ConfluencePageAiSnippetVariables;
	workspaceId: string;
	product: string;
}): Promise<ConfluencePageAiSnippetResponse | null | undefined> => {
	return fetchAggResponse<ConfluencePageAiSnippetVariables, ConfluencePageAiSnippetResponse>({
		variables,
		graphQuery: KnowledgeDiscoveryAiSnippetQuery,
		operationName: CONFLUENCE_PAGE_AI_SNIPPET_OPERATION_NAME,
		headers: createAtlAttributionHeader({
			atlWorkspaceId: workspaceId,
			tenantId: variables.cloudId,
			product,
		}),
	})
		.then((response) => response.data)
		.catch((error) => {
			logger.error({ error, functionName: 'useConfluenceAiSnippet' });
			// rethrow error to be handled by the caller
			throw error;
		});
};

export const useConfluenceAiSnippet = ({
	cloudId,
	id,
	workspaceId,
	product,
}: {
	cloudId: string;
	id: string;
	workspaceId: string;
	product: string;
}) => {
	const [loading, setIsLoading] = useState(false);
	const [error, setError] = useState(false);
	const [aiSnippet, setAiSnippet] = useState<string | undefined>();

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsLoading(true);

				const data = await fetchConfluenceAiSnippet({
					variables: {
						cloudId,
						id,
					},
					workspaceId,
					product,
				});

				setAiSnippet(data?.singleContent?.aiProperty?.objectData || '');
			} catch (e) {
				logger.error({ error: e, functionName: 'useConfluenceAiSnippet' });
				setError(true);
			} finally {
				setIsLoading(false);
			}
		};

		if (id && cloudId && workspaceId && product) {
			fetchData();
		}
	}, [cloudId, id, workspaceId, product]);

	return {
		aiSnippet,
		loading,
		error,
	};
};
